import { useI18n } from '@ecom/i18n';
import { Checkbox, Field, Input } from '@ecom/ui-kit';
import { zodResolver } from '@hookform/resolvers/zod';
import { type ReactElement } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { inviteStaffFormSchema, type InviteStaffFormValues } from '../schemas';
import { PERMISSION_KEYS, PERMISSION_LABEL_KEY } from '../types';

import { whenDefined } from '@/lib/optional-props';

export function InviteStaffForm({
  onSubmit,
  isSubmitting,
  formId = 'invite-staff-form',
}: {
  onSubmit: (values: InviteStaffFormValues) => void;
  isSubmitting: boolean;
  /** The id the modal's submit button points at (`form="..."`): one form component serves « invite » and « create an account ». */
  formId?: string;
}): ReactElement {
  const { t } = useI18n();
  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<InviteStaffFormValues>({
    resolver: zodResolver(inviteStaffFormSchema),
    defaultValues: { email: '', permissions: [] },
  });

  return (
    <form
      id={formId}
      onSubmit={(event) => {
        void handleSubmit(onSubmit)(event);
      }}
      noValidate
      className="flex flex-col gap-4"
    >
      <fieldset disabled={isSubmitting} className="contents">
        <Field
          label={t('merchant.staff.form.emailLabel')}
          {...whenDefined('error', errors.email?.message)}
        >
          {(bind) => <Input {...bind} {...register('email')} type="email" autoFocus />}
        </Field>

        <div>
          <p className="mb-2 text-sm font-medium text-ink">
            {t('merchant.staff.table.colPermissions')}
          </p>
          <Controller
            control={control}
            name="permissions"
            render={({ field }) => (
              <div className="flex flex-col gap-2">
                {PERMISSION_KEYS.map((key) => (
                  <Checkbox
                    key={key}
                    label={t(PERMISSION_LABEL_KEY[key])}
                    checked={field.value.includes(key)}
                    onChange={(checked) => {
                      field.onChange(
                        checked ? [...field.value, key] : field.value.filter((k) => k !== key),
                      );
                    }}
                  />
                ))}
              </div>
            )}
          />
        </div>

        <p className="text-xs text-inkFaint">{t('merchant.staff.form.hint')}</p>
      </fieldset>
    </form>
  );
}
